// src/resources/reports/ReportAppPenetration/types.ts
//
// Contrato do endpoint GET /dashboard-v2/penetration-details — espelha §3 do spec
// docs/superpowers/specs/2026-09-17-penetracao-app-detalhe-design.md (pontuax-backend).
// O backend está sendo escrito em paralelo com este mesmo contrato.
import { formatNumber } from '../../../utils';

/** Um conjunto de métricas por escopo (dentro do app / fora do app / total da pista). */
export interface MetricSet {
  value: number;
  volume: number;
  fillins: number;
  discounts: number | null; // null = não se aplica (desconto só existe dentro do app)
  avgTicket: number | null; // value / fillins
  avgVolume: number | null; // volume / fillins
}

export interface StationRow {
  placeId: string;
  name: string;
  app: MetricSet;
  off: MetricSet;
  total: MetricSet;
  // participação do app no total (0–1); null quando o total da métrica é zero
  share: { value: number | null; volume: number | null; fillins: number | null };
  // ganho do ticket/volume médio do app contra o fora ((app-off)/off); null sem base de comparação
  lift: { avgTicket: number | null; avgVolume: number | null };
}

export interface SeriesPoint {
  date: string; // YYYY-MM-DD
  appValue: number;
  offValue: number;
  appVolume: number;
  offVolume: number;
  appFillins: number;
  offFillins: number;
}

// Enum tipado (regra do CLAUDE.md — nunca comparar string solta): motivo de um
// posto ficar fora do cálculo por falta de dado de pista.
export enum ExclusionReason {
  NoConcentrator = 'no_concentrator',
  NoPumpData = 'no_pump_data',
}

export interface ExcludedStation {
  placeId: string;
  name: string;
  reason: ExclusionReason;
  appFillins: number;
}

export interface PenetrationDetailsData {
  id: 'penetration-details';
  available: boolean; // false = sem concentrador / sem Redshift / query falhou
  totals: StationRow; // rede inteira (só postos COM dado de pista) — placeId '0', name 'Todos'
  byStation: StationRow[]; // ordenado por share.volume desc
  series: SeriesPoint[];
  excludedStations: ExcludedStation[]; // só posto com abastecimento no app no período
  fuelTypesIgnored: string[]; // combustíveis sem fuel_type — não existem na pista
}

// Escopo de uma linha da tabela (grupo de 3 por posto). Enum tipado em vez de
// string literal solta em comparação — mesma regra do backend, aplicada aqui.
export enum RowScope {
  App = 'app',
  Off = 'off',
  Total = 'total',
}

// Métrica selecionável no gráfico diário (barras empilhadas + linha de %).
export enum ChartMetric {
  Value = 'value',
  Volume = 'volume',
  Fillins = 'fillins',
}

// As 6 métricas da tabela, na ordem em que aparecem nas colunas.
export enum MetricKey {
  Value = 'value',
  Volume = 'volume',
  Fillins = 'fillins',
  Discounts = 'discounts',
  AvgTicket = 'avgTicket',
  AvgVolume = 'avgVolume',
}

export const METRIC_ORDER: MetricKey[] = [
  MetricKey.Value, MetricKey.Volume, MetricKey.Fillins,
  MetricKey.Discounts, MetricKey.AvgTicket, MetricKey.AvgVolume,
];

export const METRIC_LABELS: Record<MetricKey, string> = {
  [MetricKey.Value]: 'Valor',
  [MetricKey.Volume]: 'Litros',
  [MetricKey.Fillins]: 'Abastecimentos',
  [MetricKey.Discounts]: 'Descontos',
  [MetricKey.AvgTicket]: 'Ticket médio',
  [MetricKey.AvgVolume]: 'Volume médio',
};

/**
 * Legenda de uma linha no cabeçalho. "Descontos" e "Volume médio" têm
 * significado específico aqui (desconto só existe dentro do app; médias são por
 * abastecimento), e o dono não deveria precisar adivinhar.
 */
export const METRIC_CAPTIONS: Record<MetricKey, string> = {
  [MetricKey.Value]: 'quanto passou pela bomba em R$',
  [MetricKey.Volume]: 'quanto saiu de combustível',
  [MetricKey.Fillins]: 'quantas vezes a bomba foi usada',
  [MetricKey.Discounts]: 'desconto dado — só existe no app',
  [MetricKey.AvgTicket]: 'R$ por abastecimento',
  [MetricKey.AvgVolume]: 'litros por abastecimento',
};

// Métricas somáveis: têm participação (%) direta na linha "No app". Ticket e
// volume médios não somam — por isso ganham o ganho (lift) em vez do %.
export const SUMMABLE_METRICS: MetricKey[] = [MetricKey.Value, MetricKey.Volume, MetricKey.Fillins];

// ─── Formatação pt-BR — sempre via formatNumber (nunca Intl solto) ────────────
export const fmtBrl = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : formatNumber(v, { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtLiters = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${formatNumber(v, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} L`;

export const fmtInt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : formatNumber(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const fmtPct = (v: number | null | undefined, decimals = 1): string =>
  v === null || v === undefined ? '—' : `${formatNumber(v * 100, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;

/** Valor de uma métrica formatado no padrão certo (moeda / litros / inteiro). */
export const formatMetricValue = (key: MetricKey, value: number | null | undefined): string => {
  switch (key) {
    case MetricKey.Value:
    case MetricKey.Discounts:
    case MetricKey.AvgTicket:
      return fmtBrl(value);
    case MetricKey.Volume:
    case MetricKey.AvgVolume:
      return fmtLiters(value);
    case MetricKey.Fillins:
      return fmtInt(value);
    default:
      return '—';
  }
};

export const metricSetFor = (row: StationRow, scope: RowScope): MetricSet => {
  switch (scope) {
    case RowScope.App: return row.app;
    case RowScope.Off: return row.off;
    case RowScope.Total: return row.total;
  }
};

/**
 * Lê o campo certo de um MetricSet a partir do enum — evita indexação solta
 * (`metrics[key]`), que o TS não aceita bem pra um enum de string contra uma
 * interface com nomes de campo fixos.
 */
export const metricValueFor = (metrics: MetricSet, key: MetricKey): number | null => {
  switch (key) {
    case MetricKey.Value: return metrics.value;
    case MetricKey.Volume: return metrics.volume;
    case MetricKey.Fillins: return metrics.fillins;
    case MetricKey.Discounts: return metrics.discounts;
    case MetricKey.AvgTicket: return metrics.avgTicket;
    case MetricKey.AvgVolume: return metrics.avgVolume;
  }
};

/** Participação (0–1) da métrica somável indicada, ou null se não se aplica. */
export const shareFor = (row: StationRow, key: MetricKey): number | null => {
  switch (key) {
    case MetricKey.Value: return row.share.value;
    case MetricKey.Volume: return row.share.volume;
    case MetricKey.Fillins: return row.share.fillins;
    default: return null;
  }
};

/**
 * Descontos não têm participação no contrato (`share` só cobre value/volume/
 * fillins, porque desconto só existe dentro do app — não há "fora" pra
 * comparar). Aqui é só contexto: quanto do valor gerado no app virou desconto.
 */
export const discountShareOfAppValue = (row: StationRow): number | null => {
  const { app } = row;
  if (app.discounts === null || !app.value) return null;
  return app.discounts / app.value;
};
